import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import styles from './AuthModal.module.css';

const AuthModal = ({ isOpen, onClose, redirectPath }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        // Validate password strength for signup
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters long');
        }
        await signup(email, password, displayName);
      }
      if (redirectPath) {
        navigate(redirectPath);
      } else {
        onClose();
      }
    } catch (error) {
      // Handle specific Firebase error codes
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email. Please sign up first.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again or reset your password.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address. Please check and try again.');
          break;
        case 'auth/email-already-in-use':
          setError('This email is already registered. Please log in instead.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please use at least 6 characters.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your internet connection.');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Please try again later.');
          break;
        case 'auth/invalid-credential':
          setError(isLogin ? 'Invalid email or password. Please try again.' : 'Invalid credentials. Please check your information.');
          break;
        default:
          setError('An error occurred. Please try again.');
      }
    }
    setLoading(false);
  }

  async function handleGoogleLogin() {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      if (redirectPath) {
        navigate(redirectPath);
      } else {
        onClose();
      }
    } catch (error) {
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          setError('Sign in was cancelled. Please try again.');
          break;
        case 'auth/popup-blocked':
          setError('Pop-up was blocked. Please allow pop-ups for this site.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your internet connection.');
          break;
        default:
          setError('Failed to sign in with Google. Please try again.');
      }
    }
    setLoading(false);
  }

  async function handleSignup(e) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signup(email, password);
      onClose();
    } catch (error) {
      if (error.message.includes("duplicate accounts")) {
        setError("You can't create duplicate accounts from the same IP Address");
      } else {
        setError('Failed to create an account');
      }
    }

    setLoading(false);
  }

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div 
            className={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <button className={styles.closeButton} onClick={onClose}>×</button>
            
            <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
            
            {error && <div className={styles.error}>{error}</div>}
            
            <button 
              className={styles.googleButton} 
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                <path fill="#FFF" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              </svg>
              Continue with Google
            </button>

            <div className={styles.divider}>or</div>
            
            <form onSubmit={isLogin ? handleSubmit : handleSignup}>
              {!isLogin && (
                <div className={styles.formGroup}>
                  <label>Display Name</label>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    className={styles.input}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Display Name"
                    required
                    autoComplete="name"
                  />
                </div>
              )}
              
              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  autoComplete="email"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className={styles.input}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  minLength="6"
                />
              </div>
              
              <button 
                className={styles.submitButton}
                type="submit"
                disabled={loading}
              >
                {isLogin ? 'Sign In' : 'Sign Up'}
              </button>
            </form>

            <div className={styles.switchText}>
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button 
                className={styles.switchButton}
                onClick={() => setIsLogin(!isLogin)}
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
